import { type FC, useState } from 'react';
import { useComments } from '@/hooks/useComments';
import { Button } from './ui/button';
import { Avatar } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { MessageCircle, Send } from 'lucide-react';
import CommentItem from './CommentItem';
import { useAuth } from '@/auth';

interface CommentsTabProps {
  tramite: any;
}

const CommentsTab: FC<CommentsTabProps> = ({ tramite }) => {
  const { user: currentUser } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyText, setReplyText] = useState('');

  const MAX_COMMENT_LENGTH = 1000;

  const {
    comments,
    isLoading,
    totalComments,
    replyingTo,
    setReplyingTo,
    addComment,
    addReply,
    updateComment,
    deleteComment,
    isAddingComment,
    isAddingReply,
    getRoleBadge,
    formatDate
  } = useComments({ tramiteId: tramite.id });

  const handleSubmitComment = () => {
    if (newComment.trim() && newComment.length <= MAX_COMMENT_LENGTH) {
      addComment(newComment);
      setNewComment('');
    }
  };

  const handleSubmitReply = (parentId: number) => {
    if (replyText.trim() && replyText.length <= MAX_COMMENT_LENGTH) {
      addReply({ parentId, replyText });
      setReplyText('');
    }
  };

  const handleReply = (commentId: number) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    if (replyingTo !== commentId) {
      setReplyText('');
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  return (
    <div className="space-y-6">
      {/* Comments list */}
      <div>
        <p className="text-sm text-blue-600 mb-6 font-medium">
          Mostrando {totalComments} comentarios (más recientes primero)
        </p>

        {isLoading ? (
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex space-x-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <Skeleton className="w-11 h-11 rounded-full" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-20 w-full" />
                  <div className="flex space-x-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
            <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="h-8 w-8 text-slate-400" />
            </div>
            <p className="text-lg font-medium text-slate-600">No hay comentarios aún</p>
            <p className="text-sm text-slate-500 mt-1">¡Sé el primero en comentar!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map(comment => (
              <div key={comment.id} className="bg-white rounded-xl border border-slate-200 transition-all duration-200">
                <CommentItem
                  comment={comment}
                  currentUserId={currentUser.id}
                  onReply={handleReply}
                  onEdit={(commentId, newComment) => updateComment({ commentId, comment: newComment })}
                  onDelete={deleteComment}
                  replyingTo={replyingTo}
                  onCancelReply={handleCancelReply}
                  replyText={replyText}
                  onReplyTextChange={(text) => {
                    if (text.length <= MAX_COMMENT_LENGTH) {
                      setReplyText(text);
                    }
                  }}
                  onSubmitReply={handleSubmitReply}
                  isAddingReply={isAddingReply}
                  getRoleBadge={getRoleBadge}
                  formatDate={formatDate}
                  maxLength={MAX_COMMENT_LENGTH}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add new comment */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <div className="flex space-x-4">
          <Avatar className="w-11 h-11 bg-blue-500 flex items-center justify-center shadow-md">
            <span className="text-sm font-medium text-white">
              {currentUser.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
            </span>
          </Avatar>
          <div className="flex-1">
            <div className="relative">
              <textarea
                value={newComment}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_COMMENT_LENGTH) {
                    setNewComment(e.target.value);
                  }
                }}
                placeholder="Agregar un comentario sobre este trámite..."
                className="w-full p-4 border border-slate-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200"
                rows={3}
              />
              <div className="absolute bottom-3 right-3 text-xs text-slate-500">
                {newComment.length}/{MAX_COMMENT_LENGTH}
              </div>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <div className="text-xs text-slate-500">
                {newComment.length > MAX_COMMENT_LENGTH - 50 && (
                  <span className={newComment.length >= MAX_COMMENT_LENGTH ? "text-red-500 font-medium" : "text-amber-600"}>
                    {newComment.length >= MAX_COMMENT_LENGTH
                      ? "Límite alcanzado"
                      : `${MAX_COMMENT_LENGTH - newComment.length} caracteres restantes`
                    }
                  </span>
                )}
              </div>
              <Button
                variant='default'
                onClick={handleSubmitComment}
                loading={isAddingComment}
                disabled={!newComment.trim() || newComment.length > MAX_COMMENT_LENGTH}
              >
                <Send size={16} className="mr-2" />
                Publicar Comentario
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CommentsTab.displayName = 'CommentsTab';

export default CommentsTab;
